import { FileSignature } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ScopePicker } from "@/components/layout/ScopePicker";
import { useDealCaptureLookups } from "@/hooks/useDealCapture";
import { areaListPath, areaPath, contractPath, locate } from "@/lib/deal-capture/paths";
import { formatNumber } from "@/lib/format";
import type { SidebarSlotProps } from "@/lib/module-registry";

import { ALL_ID, MATCHES_ID, matches, pickerOptions } from "./picker-options";

/**
 * The Contract picker, while the area is Contracts. Matches a contract's
 * number or its legal entity, busiest contract first.
 */
export function ContractPicker({ onClose }: SidebarSlotProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { data, isLoading } = useDealCaptureLookups();
  const [query, setQuery] = useState("");
  const { recordId, page } = locate(pathname);
  const contracts = useMemo(() => data?.contracts ?? [], [data]);
  const listQuery = new URLSearchParams(search).get("q") ?? "";

  const matched = useMemo(
    () =>
      contracts
        .filter((c) => matches(query, c.number, c.entity))
        .sort((a, b) => b.dealCount - a.dealCount || a.number.localeCompare(b.number))
        .map((c) => ({
          id: c.id,
          label: c.number,
          hint: `${c.entity ?? "No legal entity"} · ${formatNumber(c.dealCount)} deals`,
        })),
    [contracts, query],
  );
  const { options, footer } = pickerOptions(
    query,
    matched,
    contracts.length,
    "All contracts",
  );

  const open = contracts.find((c) => c.id === recordId);
  const selected = open
    ? { id: open.id, label: open.number }
    : page === "list"
      ? listQuery
        ? { id: MATCHES_ID, label: `Matches for “${listQuery}”` }
        : { id: ALL_ID, label: `All contracts · ${formatNumber(contracts.length)}` }
      : null;

  return (
    <ScopePicker
      axis="Contract"
      icon={FileSignature}
      selected={selected}
      placeholder="Find a contract"
      searchPlaceholder={`Search ${formatNumber(contracts.length)} contracts by number or entity`}
      emptyMessage="No contracts match that search."
      options={options}
      isLoading={isLoading}
      footer={footer}
      onSearch={setQuery}
      onSelect={(id) => {
        if (id === ALL_ID) navigate(areaListPath("contracts"));
        else if (id === MATCHES_ID) {
          navigate(`${areaListPath("contracts")}?${new URLSearchParams({ q: query.trim() })}`);
        } else navigate(contractPath(id));
        onClose();
      }}
      onClear={() => {
        navigate(areaPath("contracts"));
        onClose();
      }}
    />
  );
}
